import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ReplaceStrings from '../../config/replaceStrings';
import Routes from '../../config/routes';
import { insightsScoreText } from './insightsAccess';
import { ClubMatchView, MatchResult } from './insightsClubSummary';
import {
    buildPlayerStats,
    buildTeamStats,
    PlayerMatchStats,
    shootingPercent,
    TeamMatchStats,
} from './insightsMatchStats';
import { loadMatchBundles, MatchBundle } from './insightsQueries';

interface Props {
    lastMatch?: ClubMatchView;
    teamIds: number[];
    loadingSummary: boolean;
}

interface SidePair {
    us: TeamMatchStats;
    them: TeamMatchStats;
}

const matchHref = (matchId: number): string =>
    Routes.ROUTE_INSIGHTS_MATCH.replace(ReplaceStrings.MATCH_ID, matchId.toString());

const playerHref = (playerId: number): string =>
    Routes.ROUTE_INSIGHTS_PLAYER.replace(ReplaceStrings.PLAYER_ID, playerId.toString());

const resultWord = (result?: MatchResult): string =>
    result === 'W' ? 'Win' : result === 'L' ? 'Loss' : result === 'D' ? 'Draw' : 'Result';

const pctText = (goals: number, shots: number): string => {
    const pct = shootingPercent(goals, shots);
    return pct == null ? '-' : `${pct}%`;
};

/**
 * Overview last-match snapshot: us vs them event totals + our top scorers.
 * One getAnalysis call via the shared bundle cache.
 */
const InsightsLastMatchPanel: React.FC<Props> = ({ lastMatch, teamIds, loadingSummary }) => {
    const [bundle, setBundle] = useState<MatchBundle | undefined>();
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            if (!lastMatch) {
                setBundle(undefined);
                setFailed(false);
                return;
            }

            setLoading(true);
            setFailed(false);
            try {
                const bundles = await loadMatchBundles([lastMatch.match]);
                if (cancelled) return;
                setBundle(bundles[0]);
                if (!bundles[0]) setFailed(true);
            } catch {
                if (!cancelled) {
                    setBundle(undefined);
                    setFailed(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [lastMatch]);

    const sides = useMemo((): SidePair | undefined => {
        if (!bundle || !lastMatch) return undefined;
        const { home, away } = buildTeamStats(bundle.detail, bundle.events);
        return lastMatch.isHome ? { us: home, them: away } : { us: away, them: home };
    }, [bundle, lastMatch]);

    const topScorers = useMemo((): Array<PlayerMatchStats & { playerId?: number }> => {
        if (!bundle || !lastMatch) return [];
        const playerIdByMatchPlayer = new Map<number, number>();
        (bundle.detail.players || []).forEach((mp) => {
            if (mp.id != null && mp.playerId != null) {
                playerIdByMatchPlayer.set(mp.id, mp.playerId);
            }
        });

        return buildPlayerStats(bundle.detail, bundle.events)
            .filter((row) => teamIds.includes(row.teamId) && row.goals > 0)
            .sort((a, b) => b.goals - a.goals || b.shots - a.shots)
            .slice(0, 3)
            .map((row) => ({
                ...row,
                playerId: playerIdByMatchPlayer.get(row.matchPlayerId),
            }));
    }, [bundle, lastMatch, teamIds]);

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Last match</h2>
                <span>
                    {lastMatch?.match.competitionDisplayName ||
                        (loadingSummary ? 'Loading...' : 'No finished match yet')}
                </span>
            </div>

            {(loadingSummary || loading) && (
                <p className="insightsSummaryMuted">Loading last match...</p>
            )}

            {!loadingSummary && !loading && !lastMatch && (
                <p className="insightsSummaryMuted">
                    No finished match yet. Play and log one to see the review here.
                </p>
            )}

            {!loadingSummary && !loading && lastMatch && failed && (
                <p className="insightsSummaryMuted">
                    Could not load events for{' '}
                    <Link className="insightsSummaryLink" to={matchHref(lastMatch.match.id)}>
                        {insightsScoreText(lastMatch.match)}
                    </Link>
                    .
                </p>
            )}

            {!loadingSummary && !loading && lastMatch && sides && (
                <>
                    <div className="insightsLastMatchHead">
                        <Link className="insightsSummaryLink" to={matchHref(lastMatch.match.id)}>
                            <span
                                className={`insightsResultBadge is-${
                                    lastMatch.result?.toLowerCase() || 'none'
                                }`}
                            >
                                {resultWord(lastMatch.result)}
                            </span>
                            <strong className="insightsSummaryScore">
                                {insightsScoreText(lastMatch.match)}
                            </strong>
                            <span className="insightsSummaryOpponent">
                                {lastMatch.isHome ? 'vs' : 'at'} {lastMatch.opponentName}
                            </span>
                        </Link>
                        <span className="insightsSummaryMeta">
                            {lastMatch.match.startDate
                                ? moment(lastMatch.match.startDate).format('DD MMM YYYY')
                                : ''}
                            {' · '}
                            <Link to={matchHref(lastMatch.match.id)}>Full match</Link>
                        </span>
                    </div>

                    <div className="insightsStatStrip" aria-label="Last match us vs them">
                        <div className="insightsStat">
                            <b>
                                {sides.us.goals}:{sides.them.goals}
                            </b>
                            <i>Goals</i>
                        </div>
                        <div className="insightsStat">
                            <b>
                                {sides.us.shots}:{sides.them.shots}
                            </b>
                            <i>Shots</i>
                        </div>
                        <div className="insightsStat">
                            <b>
                                {pctText(sides.us.goals, sides.us.shots)} /{' '}
                                {pctText(sides.them.goals, sides.them.shots)}
                            </b>
                            <i>Shoot %</i>
                        </div>
                        <div className="insightsStat">
                            <b>
                                {sides.us.saves}:{sides.them.saves}
                            </b>
                            <i>Saves</i>
                        </div>
                        <div className="insightsStat">
                            <b>
                                {sides.us.exclusions}:{sides.them.exclusions}
                            </b>
                            <i>Exclusions</i>
                        </div>
                        <div className="insightsStat">
                            <b>
                                {sides.us.turnovers}:{sides.them.turnovers}
                            </b>
                            <i>Turnovers</i>
                        </div>
                    </div>
                    <p className="insightsTableNote">Us : Them in every cell.</p>

                    {topScorers.length > 0 && (
                        <ol className="insightsLeaders insightsLastMatchScorers">
                            {topScorers.map((row) => (
                                <li key={row.matchPlayerId} className="insightsLeaderRow">
                                    {row.playerId != null ? (
                                        <Link
                                            className="insightsMatchLink"
                                            to={playerHref(row.playerId)}
                                        >
                                            <span className="insightsLeaderName">
                                                #{row.capNumber} {row.name}
                                            </span>
                                            <span className="insightsLeaderMeta">
                                                {row.shots} shots · {pctText(row.goals, row.shots)}
                                            </span>
                                        </Link>
                                    ) : (
                                        <span className="insightsMatchLink">
                                            <span className="insightsLeaderName">
                                                #{row.capNumber} {row.name}
                                            </span>
                                            <span className="insightsLeaderMeta">
                                                {row.shots} shots · {pctText(row.goals, row.shots)}
                                            </span>
                                        </span>
                                    )}
                                    <b>{row.goals}</b>
                                </li>
                            ))}
                        </ol>
                    )}
                </>
            )}
        </section>
    );
};

export default InsightsLastMatchPanel;
